import { useQuery } from '@tanstack/react-query';
import { fetchDashboardSummary } from '../../lib/api/dashboard';
import { queryKeys } from '../../lib/query-client';
import { useFilters } from '../../hooks/useFilters';
import { DataBoundary } from '../../components/DataBoundary';
import { ChartCard } from '../../components/ChartCard';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiGrid } from '../../components/KpiGrid';
import { ProvenanceBar } from '../../components/ProvenanceBar';
import { ChartSkeleton, KpiSkeleton } from '../../components/primitives';
import { PageHeading } from '../layout/PageHeading';

/**
 * Dashboard ejecutivo.
 *
 * Los 15 KPI y los 12 gráficos llegan ya calculados en `GET /api/dashboard/summary`
 * (DEC-A04): el frontend recorre `kpis` y `charts` sin conocer cuántos son ni qué
 * fórmula los produjo. Añadir un indicador es un cambio de backend, no de interfaz.
 *
 * Los dos primeros gráficos ocupan la fila ancha, como en el dashboard original.
 */
export function ExecutiveDashboardPage(): JSX.Element {
  const { filters } = useFilters();

  const summaryQuery = useQuery({
    queryKey: queryKeys.dashboard(filters),
    queryFn: () => fetchDashboardSummary(filters),
  });

  const summary = summaryQuery.data;
  const charts = summary?.charts ?? [];
  const [leadChart, secondChart, ...restCharts] = charts;

  return (
    <>
      <PageHeading
        title="Dashboard ejecutivo"
        description="Visión consolidada de Gestión de Personas. Cada cifra indica su estado: los datos parciales se declaran y la ausencia de registros no se disfraza de porcentaje."
        actions={<ExportMenu moduleKey="dashboard" />}
      />

      <DataBoundary
        isLoading={summaryQuery.isLoading}
        isError={summaryQuery.isError}
        error={summaryQuery.error}
        onRetry={() => void summaryQuery.refetch()}
        isEmpty={summary !== undefined && summary.kpis.length === 0 && charts.length === 0}
        empty={{
          title: 'Sin datos para el período seleccionado',
          message:
            'No hay indicadores calculados con los filtros aplicados. Revise el período elegido o el estado de la sincronización con BUK.',
        }}
        warnings={summary?.provenance.warnings}
        coverage={summary?.provenance.coverage}
        skeleton={
          <>
            <KpiSkeleton count={12} />
            <div className="row row--2">
              <ChartSkeleton />
              <ChartSkeleton />
            </div>
            <div className="row row--3">
              <ChartSkeleton height={224} />
              <ChartSkeleton height={224} />
              <ChartSkeleton height={224} />
            </div>
          </>
        }
      >
        {summary !== undefined && (
          <>
            <ProvenanceBar provenance={summary.provenance} />

            <KpiGrid kpis={summary.kpis} linkable />

            {(leadChart !== undefined || secondChart !== undefined) && (
              <div className="row row--2">
                {leadChart !== undefined && <ChartCard payload={leadChart} />}
                {secondChart !== undefined && <ChartCard payload={secondChart} />}
              </div>
            )}

            {restCharts.length > 0 && (
              <div className="row row--2e">
                {restCharts.map((chart) => (
                  <ChartCard key={chart.key} payload={chart} small />
                ))}
              </div>
            )}
          </>
        )}
      </DataBoundary>
    </>
  );
}
